/**
 * Case Strength Indicator Component
 * 
 * Displays the automation-calculated case strength score with visual indicators.
 * Part of the 90% Automation System visibility UI.
 */

import React from 'react';

interface CaseStrengthIndicatorProps {
  score: number;  // 0-100
  isReady: boolean;
  isAutoReady?: boolean;
  blocking?: number;
  compact?: boolean;
  showDetails?: boolean;
}

export const CaseStrengthIndicator: React.FC<CaseStrengthIndicatorProps> = ({
  score,
  isReady,
  isAutoReady = false,
  blocking = 0,
  compact = false,
  showDetails = false
}) => {
  // Color based on score
  const getScoreColor = (): React.CSSProperties => {
    if (score >= 90) return { color: 'var(--color-verified)' };
    if (score >= 70) return { color: 'var(--color-needs-human-text)' };
    if (score >= 50) return { color: 'var(--color-brand)' };
    return { color: 'var(--color-needs-human-text)' };
  };

  const getBackgroundColor = (): React.CSSProperties => {
    if (score >= 90) return { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' };
    if (score >= 70) return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    if (score >= 50) return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
  };

  const getProgressColor = (): React.CSSProperties => {
    if (score >= 90) return { backgroundColor: 'var(--color-verified)' };
    if (score >= 70) return { backgroundColor: 'var(--color-brand)' };
    if (score >= 50) return { backgroundColor: 'var(--color-brand)' };
    return { backgroundColor: 'var(--color-brand)' };
  };

  const getStatusText = () => {
    if (isAutoReady) return 'Auto-Ready';
    if (isReady) return 'Ready';
    if (blocking > 0) return `${blocking} Blocking`;
    if (score >= 70) return 'In Progress';
    return 'Needs Work';
  };

  const getStatusBadgeColor = (): React.CSSProperties => {
    if (isAutoReady) return { backgroundColor: 'transparent', color: 'var(--color-verified)' };
    if (isReady) return { backgroundColor: 'transparent', color: 'var(--color-verified)' };
    if (blocking > 0) return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
    if (score >= 70) return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
    return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' };
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="text-lg font-bold" style={getScoreColor()}>
          {score}
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full" style={getStatusBadgeColor()}>
          {getStatusText()}
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4" style={getBackgroundColor()}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Case Strength</span>
        </div>
        <span className="text-sm px-2.5 py-1 rounded-full font-medium" style={getStatusBadgeColor()}>
          {getStatusText()}
        </span>
      </div>

      {/* Score Display */}
      <div className="flex items-center gap-4">
        <div className="text-4xl font-bold" style={getScoreColor()}>
          {score}
        </div>
        <div className="flex-1">
          {/* Progress Bar */}
          <div className="w-full rounded-full h-3 mb-1" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <div
              className="h-3 rounded-full transition-all duration-500"
              style={{ width: `${score}%`, ...getProgressColor() }}
            />
          </div>
          <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
            <span>0</span>
            <span style={{ color: 'var(--color-text-dim)' }}>Ready: 90</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center">
              <div style={{ color: 'var(--color-text-muted)' }}>Blocking</div>
              <div className="font-bold" style={{ color: blocking > 0 ? 'var(--color-needs-human-text)' : 'var(--color-verified)' }}>
                {blocking}
              </div>
            </div>
            <div className="text-center">
              <div style={{ color: 'var(--color-text-muted)' }}>Ready</div>
              <div className="font-bold" style={{ color: isReady ? 'var(--color-verified)' : 'var(--color-text-dim)' }}>
                {isReady ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="text-center">
              <div style={{ color: 'var(--color-text-muted)' }}>Auto-Ready</div>
              <div className="font-bold" style={{ color: isAutoReady ? 'var(--color-verified)' : 'var(--color-text-dim)' }}>
                {isAutoReady ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStrengthIndicator;
